/**
 * v4.11.0 — Fleet Filters strip.
 *
 * Server-driven filtering. Five inputs total (org/loc/status/search +
 * bulk-actions stub) — every value pushes back through `onChange`. No
 * client-side filtering; the parent page re-queries.
 *
 * Bulk Actions button surfaces a placeholder menu and explicitly
 * defers the actual mutation flow to Phase 2 to keep scope tight.
 */
import { useEffect, useState } from "react";
import { sortByName } from '../../lib/sortByName';
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { Search, SlidersHorizontal } from "lucide-react";

const Select = ({ value, onChange, options, label, testId }) => (
    <select
        data-testid={testId}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border bg-background text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-sky-400/40"
        style={{ borderColor: "rgba(148, 163, 184, 0.2)" }}
    >
        {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
        ))}
    </select>
);

export default function FleetFilters({ filters, onChange, showOrgFilter, scopeOrgId = '' }) {
    const { t } = useTranslation();
    const [orgs, setOrgs] = useState([]);
    const [locs, setLocs] = useState([]);

    useEffect(() => {
        if (!showOrgFilter) return;
        api.get("/organizations").then((r) => {
            const list = Array.isArray(r?.data) ? r.data : (r?.data?.organizations || []);
            setOrgs(sortByName(list));
        }).catch(() => { /* render-safe — empty list */ });
    }, [showOrgFilter]);

    useEffect(() => {
        api.get("/locations").then((r) => {
            const list = Array.isArray(r?.data) ? r.data : (r?.data?.locations || []);
            setLocs(sortByName(list));
        }).catch(() => {});
    }, []);

    // When the screen is scoped to one organization, only ITS locations may be
    // offered — otherwise the operator can pick their way out of the scope.
    const visibleLocs = scopeOrgId
        ? locs.filter((l) => l.organization_id === scopeOrgId)
        : locs;

    const statusOpts = [
        { value: "", label: t("card_fleet.filter.all_status") },
        { value: "active", label: t("card_fleet.status.active") },
        { value: "inactive", label: t("card_fleet.status.inactive") },
        { value: "paused", label: t("card_fleet.status.paused") },
        { value: "unassigned", label: t("card_fleet.status.unassigned") },
    ];

    return (
        <div
            className="flex flex-wrap items-center gap-2 sm:gap-3 p-3 rounded-xl border"
            style={{
                background: "rgba(15, 23, 42, 0.45)",
                borderColor: "rgba(148, 163, 184, 0.12)",
            }}
            data-testid="fleet-filters"
            data-insight-id="fleet.filters"
            data-insight-label="Fleet filters"
        >
            {showOrgFilter && (
                <div className="min-w-[180px] flex-1 sm:flex-none">
                    <Select
                        value={filters.organization_id}
                        onChange={(v) => onChange({ organization_id: v })}
                        options={[{ value: "", label: t("card_fleet.filter.all_orgs") },
                            ...orgs.map((o) => ({ value: o.id, label: o.name }))]}
                        label={t("card_fleet.filter.all_orgs")}
                        testId="filter-org"
                    />
                </div>
            )}
            <div className="min-w-[180px] flex-1 sm:flex-none">
                <Select
                    value={filters.location_id}
                    onChange={(v) => onChange({ location_id: v })}
                    options={[{ value: "", label: t("card_fleet.filter.all_locations") },
                        ...visibleLocs.map((l) => ({ value: l.id, label: l.name }))]}
                    label={t("card_fleet.filter.all_locations")}
                    testId="filter-location"
                />
            </div>
            <div className="min-w-[150px] flex-1 sm:flex-none">
                <Select
                    value={filters.status}
                    onChange={(v) => onChange({ status: v })}
                    options={statusOpts}
                    label={t("card_fleet.filter.all_status")}
                    testId="filter-status"
                />
            </div>

            <div className="flex-1 min-w-[200px] relative">
                <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                    type="search"
                    value={filters.search}
                    onChange={(e) => onChange({ search: e.target.value })}
                    placeholder={t("card_fleet.filter.search_placeholder")}
                    data-testid="filter-search"
                    className="w-full rounded-lg border bg-background text-foreground pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-sky-400/40"
                    style={{ borderColor: "rgba(148, 163, 184, 0.2)" }}
                />
            </div>

            <button
                type="button"
                onClick={() => onChange({
                    // Reset clears the operator's own filters — it must NEVER
                    // clear the Command Center's organization.
                    organization_id: scopeOrgId || "",
                    location_id: "",
                    status: "",
                    mode: "",
                    schedule: "",
                    search: "",
                })}
                data-testid="filter-reset"
                className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
                title={t("card_fleet.filter.reset")}
            >
                <SlidersHorizontal size={13} strokeWidth={1.75} />
                {t("card_fleet.filter.reset")}
            </button>
        </div>
    );
}
